import React, { useState, useEffect, useCallback } from 'react';
import { crmAPI } from '../../lib/api';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Skeleton } from '../ui/skeleton';
import { ScrollArea } from '../ui/scroll-area';
import { Search, RefreshCw, AlertTriangle, CheckCircle, Clock, ChevronLeft, ChevronRight, Zap, TrendingUp, User, Tag } from 'lucide-react';
import { toast } from 'sonner';

const SALES_ACTIVITY_TYPES = [
  "Demo", "RFP Submission", "Proof of concept",
  "Vendor Meeting", "Product Presentation", "POC", "Vendor Presentation"
];

const TYPE_ICONS = {
  call: '📞', email: '✉️', meeting: '🤝',
  demo: '🖥️', 'proof of concept': '🔬', 'site visit': '🏢',
  'work shop': '🛠️', 'rfp submission': '📄', 'vendor meeting': '🤝',
  'product presentation': '📊', poc: '🔬', 'vendor presentation': '📊',
  'to do': '📋',
};
const TYPE_COLORS = {
  call: 'bg-blue-100 text-blue-700',
  email: 'bg-purple-100 text-purple-700',
  meeting: 'bg-amber-100 text-amber-700',
  demo: 'bg-indigo-100 text-indigo-700',
  'proof of concept': 'bg-cyan-100 text-cyan-700',
  'rfp submission': 'bg-rose-100 text-rose-700',
  'vendor meeting': 'bg-teal-100 text-teal-700',
  'product presentation': 'bg-violet-100 text-violet-700',
  poc: 'bg-cyan-100 text-cyan-700',
  'vendor presentation': 'bg-violet-100 text-violet-700',
  'to do': 'bg-gray-100 text-gray-600',
};

const PAGE_SIZE = 80;

function SalesActivityBadge({ className = '' }) {
  return (
    <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[9px] font-bold bg-[#800000]/10 text-[#800000] border border-[#800000]/20 ${className}`}>
      <Zap className="h-2.5 w-2.5" />
      Sales Activity
    </span>
  );
}

export function ActivityTimelinePage() {
  const [records, setRecords] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterYear, setFilterYear] = useState(''); // Default: all years (most data is 2025, not 2026)
  const [overdueOnly, setOverdueOnly] = useState(false);
  const [salesOnly, setSalesOnly] = useState(false); // Default: show all activity types
  const [filterSalesperson, setFilterSalesperson] = useState('');

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: PAGE_SIZE, skip: page * PAGE_SIZE };
      if (filterType !== 'all') params.activity_type = filterType;
      if (filterYear && filterYear !== 'all') params.year = filterYear;
      if (overdueOnly) params.overdue_only = true;
      if (salesOnly) params.sales_only = true;
      if (filterSalesperson) params.salesperson = filterSalesperson;

      const [reportRes, statsRes] = await Promise.allSettled([
        crmAPI.getActivityReport(params),
        crmAPI.getActivityReportStats(filterYear && filterYear !== 'all' ? { year: filterYear, sales_only: salesOnly } : { sales_only: salesOnly }),
      ]);

      if (reportRes.status === 'fulfilled') {
        let recs = reportRes.value.data?.records || [];
        // If 2026 returns 0 results, auto-fallback to previous year with data
        if (filterYear === '2026' && recs.length === 0) {
          const fallbackRes = await crmAPI.getActivityReport({ ...params, year: '2025' });
          if ((fallbackRes.data?.records || []).length > 0) {
            recs = fallbackRes.data.records;
            setFilterYear('2025');
          }
        }
        setRecords(recs);
        setTotal(reportRes.value.data?.total || 0);
      }
      if (statsRes.status === 'fulfilled') setStats(statsRes.value.data);
    } catch (e) {
      toast.error('Failed to load activities');
    } finally {
      setLoading(false);
    }
  }, [page, filterType, filterYear, overdueOnly, salesOnly, filterSalesperson]);

  useEffect(() => { loadData(); }, [loadData]);

  const filtered = records.filter(r => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (r.assigned_to || '').toLowerCase().includes(q) ||
           (r.salesperson || '').toLowerCase().includes(q) ||
           (r.opportunity_name || '').toLowerCase().includes(q) ||
           (r.body || '').toLowerCase().includes(q) ||
           (r.customer || '').toLowerCase().includes(q);
  });

  const totalPages = Math.ceil(total / PAGE_SIZE);

  return (
    <div className="space-y-4 p-1" data-testid="activity-timeline-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            Activities
            {salesOnly && <SalesActivityBadge />}
          </h1>
          <p className="text-gray-400 text-xs mt-0.5">
            {total.toLocaleString()} {salesOnly ? 'sales activities' : 'activities'}
            {stats && ` · ${stats.overdue_total} overdue`}
            {stats?.sales_activity_total !== undefined && !salesOnly && ` · ${stats.sales_activity_total} sales`}
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={loadData}>
          <RefreshCw className="h-4 w-4 mr-1" /> Refresh
        </Button>
      </div>

      {/* Stats row */}
      {stats && (
        <div className="grid grid-cols-4 gap-3">
          <div className="bg-white rounded-lg border p-3 text-center">
            <p className="text-2xl font-bold text-gray-900">{stats.total?.toLocaleString()}</p>
            <p className="text-xs text-gray-500">{salesOnly ? 'Sales Activities' : 'Total Activities'}</p>
          </div>
          <div className="bg-[#800000]/5 rounded-lg border border-[#800000]/20 p-3 text-center">
            <p className="text-2xl font-bold text-[#800000]">{stats.sales_activity_total?.toLocaleString() || (salesOnly ? stats.total : '–')}</p>
            <p className="text-xs text-[#800000]">Sales Activities</p>
          </div>
          <div className="bg-emerald-50 rounded-lg border border-emerald-200 p-3 text-center">
            <p className="text-2xl font-bold text-emerald-700">{stats.on_time_total?.toLocaleString()}</p>
            <p className="text-xs text-emerald-600">On Time</p>
          </div>
          <div className="bg-red-50 rounded-lg border border-red-200 p-3 text-center">
            <p className="text-2xl font-bold text-red-700">{stats.overdue_total?.toLocaleString()}</p>
            <p className="text-xs text-red-600">Overdue</p>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap gap-2 items-center">
        {/* Sales Activity Toggle — primary filter */}
        <Button
          size="sm"
          variant={salesOnly ? 'default' : 'outline'}
          className={`h-8 text-xs font-semibold ${salesOnly ? 'bg-[#800000] hover:bg-[#9a1919] text-white' : 'border-[#800000]/30 text-[#800000] hover:bg-[#800000]/5'}`}
          onClick={() => { setSalesOnly(!salesOnly); setPage(0); }}
          data-testid="sales-only-filter"
        >
          <Zap className="h-3.5 w-3.5 mr-1" />
          Sales Activities Only
          {salesOnly && <span className="ml-1 bg-white/20 rounded px-1">{total}</span>}
        </Button>

        <div className="h-6 border-l border-gray-200" />

        <div className="relative min-w-[180px]">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
          <Input value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
            placeholder="Search..." className="pl-8 h-8 text-xs" data-testid="activity-search" />
        </div>
        <Select value={filterType} onValueChange={v => { setFilterType(v); setPage(0); }}>
          <SelectTrigger className="w-[140px] h-8 text-xs">
            <SelectValue placeholder="Type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            {salesOnly
              ? SALES_ACTIVITY_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)
              : ['Call', 'Email', 'Meeting', 'To Do', ...SALES_ACTIVITY_TYPES].map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)
            }
          </SelectContent>
        </Select>
        <Select value={filterYear} onValueChange={v => { setFilterYear(v === 'all' ? '' : v); setPage(0); }}>
          <SelectTrigger className="w-[90px] h-8 text-xs">
            <SelectValue placeholder="Year" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Years</SelectItem>
            {['2026', '2025', '2024'].map(y => <SelectItem key={y} value={y}>{y}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button size="sm" variant={overdueOnly ? 'default' : 'outline'} className="h-8 text-xs"
          onClick={() => { setOverdueOnly(!overdueOnly); setPage(0); }}>
          <AlertTriangle className="h-3.5 w-3.5 mr-1" /> Overdue
        </Button>
        <Input value={filterSalesperson} onChange={e => setFilterSalesperson(e.target.value)}
          placeholder="Salesperson..." className="w-[140px] h-8 text-xs" />
      </div>

      {/* Type breakdown (sales activities) */}
      {stats?.by_type?.length > 0 && salesOnly && (
        <div className="flex flex-wrap gap-2">
          {stats.by_type.filter(t => t.is_sales).map(t => (
            <button key={t.type}
              onClick={() => setFilterType(filterType === t.type ? 'all' : t.type)}
              className={`text-xs px-2.5 py-1 rounded-full border transition-all ${filterType === t.type ? 'bg-[#800000] text-white border-[#800000]' : 'bg-gray-50 text-gray-600 border-gray-200 hover:border-[#800000]/40'}`}>
              {t.type}: <strong>{t.count}</strong>
            </button>
          ))}
        </div>
      )}

      {/* Visual Vertical Timeline */}
      {loading ? (
        <div className="space-y-3">{[...Array(5)].map((_, i) => <Skeleton key={i} className="h-16 w-full" />)}</div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-12 text-gray-400">
          <TrendingUp className="h-8 w-8 mx-auto mb-2 opacity-40" />
          <p className="text-sm">No activities found</p>
        </div>
      ) : (
        <div className="relative" data-testid="visual-timeline">
          {/* Vertical line */}
          <div className="absolute left-[88px] top-0 bottom-0 w-0.5 bg-gradient-to-b from-[#800000]/40 via-muted to-transparent" />

          <ScrollArea className="h-[560px] pr-2">
            <div className="space-y-0 pb-8">
              {filtered.map((act, idx) => {
                const typeLower = (act.activity_type || '').toLowerCase();
                const typeColor = TYPE_COLORS[typeLower] || 'bg-gray-100 text-gray-600';
                const typeIcon = TYPE_ICONS[typeLower] || '📋';
                const isSales = act.is_sales_activity;
                const dateStr = act.completion_date || act.due_date || '';
                const dateFormatted = dateStr
                  ? new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: '2-digit' })
                  : '—';
                const timeFormatted = act.completion_date
                  ? new Date(act.completion_date).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false })
                  : '';

                // Group by date — add date separator
                const prevDate = idx > 0 ? (filtered[idx-1].completion_date || filtered[idx-1].due_date || '').slice(0,10) : '';
                const currDate = dateStr.slice(0,10);
                const showDateSep = currDate !== prevDate && currDate;

                return (
                  <div key={act.source_record_id || idx}>
                    {/* Date separator */}
                    {showDateSep && (
                      <div className="flex items-center gap-3 my-3 pl-4">
                        <span className="text-[10px] font-bold text-muted-foreground uppercase tracking-wide w-16 text-right">{dateFormatted}</span>
                        <div className="flex-1 h-px bg-border" />
                      </div>
                    )}

                    {/* Timeline entry */}
                    <div className={`relative flex items-start gap-3 pl-4 py-2 hover:bg-muted/20 rounded-r-lg transition-colors ${isSales ? 'border-l-2 border-[#800000] -ml-0.5' : ''}`}
                      data-testid={`timeline-row-${idx}`}>

                      {/* Time stamp (left) */}
                      <div className="w-16 text-right shrink-0">
                        <p className="text-[10px] font-mono text-muted-foreground leading-tight">{timeFormatted || dateFormatted}</p>
                      </div>

                      {/* Node dot on the line */}
                      <div className={`relative z-10 shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-sm border-2 border-white shadow-sm ${isSales ? 'bg-[#800000] text-white' : 'bg-muted'}`}>
                        {typeIcon}
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0 pb-1">
                        <div className="flex items-start justify-between gap-2 flex-wrap">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${typeColor}`}>{act.activity_type || 'Activity'}</span>
                            {isSales && <SalesActivityBadge />}
                            {act.was_overdue && <span className="text-[9px] text-red-600 bg-red-50 px-1.5 py-0.5 rounded">Late +{act.days_late}d</span>}
                          </div>
                          {act.was_overdue === false && <span className="text-[9px] text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded">On Time</span>}
                        </div>

                        {/* Main text */}
                        {act.body && (
                          <p className="text-xs text-gray-700 mt-0.5 line-clamp-2">{act.body}</p>
                        )}

                        {/* Meta row */}
                        <div className="flex items-center gap-3 mt-1 text-[10px] text-muted-foreground flex-wrap">
                          <span className="flex items-center gap-0.5">
                            <User className="h-2.5 w-2.5" />{act.assigned_to || act.salesperson || '—'}
                          </span>
                          {act.opportunity_name && (
                            <span className="flex items-center gap-0.5 truncate max-w-40">
                              <Tag className="h-2.5 w-2.5" />{act.opportunity_name}
                            </span>
                          )}
                          {act.customer && <span className="text-muted-foreground/70">{act.customer}</span>}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </ScrollArea>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between text-xs text-gray-500">
          <span>{page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, total)} of {total.toLocaleString()}</span>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" disabled={page === 0} onClick={() => setPage(p => p - 1)} className="h-7 text-xs">
              <ChevronLeft className="h-3.5 w-3.5" /> Prev
            </Button>
            <Button size="sm" variant="outline" disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)} className="h-7 text-xs">
              Next <ChevronRight className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
